import { detectEnv } from '@/shared/detect';
import yxhJsBridge, { type ShareType } from '@/shared/yxhJsBridge';
import { Popup, Toast } from 'vant';
import wechatFriendsImage from '@/assets/img/share/wechat-friends.svg';
import wechatZoneImage from '@/assets/img/share/wechat-zone.svg';
import linkImage from '@/assets/img/share/link.svg';
import { getCurrentDomain } from '@/shared/util';
import SATracking, { SATrackingPlugin } from '@/shared/saTracking';
import type { CombinedVueInstance } from 'vue/types/vue';
import {
  type PluginObject,
  type VueConstructor,
  getCurrentInstance,
  reactive,
  ref,
  watch,
} from 'vue';
import styles from './style.module.css';

export interface SetupConfig {
  headline: string;
  title: string;
  desc: string;
  link: string;
  image: string;
  success: () => void;
  fail: (err: Error) => void;
}

export type SharerMethods = {
  setup: (config: Partial<SetupConfig>) => void;
  show: () => void;
  hide: () => void;
};

const enum ShareTypes {
  WECHAT_FRIENDS = 1,
  WECHAT_ZONE = 2,
  LINK = 3,
}

let instance: CombinedVueInstance<
  Vue,
  Record<string, any>,
  SharerMethods,
  Record<never, never>,
  Record<never, never>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const noop = (...args: any): void => {};

function createInstance(Vue: VueConstructor) {
  if (instance) return instance;

  Vue.use(SATrackingPlugin);

  instance = new Vue({
    name: 'Sharer',
    setup() {
      const instance = getCurrentInstance();

      const state = reactive({
        headline: '',
        title: '',
        desc: '',
        link: '',
        image: '',
        success: () => {},
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        fail: (err: Error) => {},
      });

      const visible = ref(false);
      watch(visible, (v) => {
        SATracking.trackPopup({
          desc: '分享入口弹窗',
          describe: SATracking.serialize({
            active_name: v ? '弹窗展示' : '弹窗关闭',
          }),
        });
      });

      // @exposed-api
      const setup = (config: Partial<SetupConfig>) => {
        state.headline = config.headline ?? '分享给好友';
        state.title = config.title ?? document.title;
        state.desc = config.desc ?? '';
        state.link = config.link ?? document.location.href;

        let image = '';
        if (config.image) {
          if (/^https?:\/\//.test(config.image)) {
            image = config.image;
          } else {
            image = `${window.location.origin}${config.image}`;
          }
        } else {
          image = `${getCurrentDomain()}img/icons/apple-touch-icon-180x180.png`;
        }
        state.image = image;
        state.success = config.success ?? noop;
        state.fail = config.fail ?? noop;
      };

      // @exposed-api
      const show = () => {
        // 仅宜享花APP中有效
        if (detectEnv.yxh) {
          visible.value = true;
        }
      };

      // @exposed-api
      const hide = () => {
        // 仅宜享花APP中有效
        if (detectEnv.yxh) {
          visible.value = false;
        }
      };

      const onInput = (value: boolean) => {
        visible.value = value;
      };

      const onCopyLink = () => {
        instance?.proxy
          .$copyText(state.link)
          .then(() => {
            Toast.success('复制成功');
            hide();
            state.success();
          })
          .catch((e: Error) => {
            Toast.success('复制失败');
            state.fail(e);
          });
      };

      const onClickItem = async (type: ShareTypes) => {
        let shareType: ShareType[] | null = null;

        switch (type) {
          case ShareTypes.WECHAT_FRIENDS:
            shareType = ['wxFriend'];
            break;
          case ShareTypes.WECHAT_ZONE:
            shareType = ['wxZone'];
            break;
        }

        if (shareType == null) {
          Toast.fail('无效的分享方式');
          return;
        }

        yxhJsBridge
          .share({
            title: state.title,
            content: state.desc,
            url: state.link,
            picUrl: state.image,
            shareType,
          })
          .then(() => {
            hide();
            state.success();
          })
          .catch((err: Error) => {
            state.fail(err);
          });
      };

      return {
        state,
        visible,
        setup,
        show,
        hide,
        onInput,
        onCopyLink,
        onClickItem,
      };
    },
    render() {
      return (
        <Popup
          value={this.visible}
          onInput={this.onInput}
          closeable
          round
          close-icon-position="top-left"
          position="bottom"
          class={styles.share}
        >
          <h2 class={styles.headline}>{this.state.headline}</h2>
          <div class={styles.content}>
            <button
              type="button"
              class={styles.button}
              onClick={() => this.onClickItem(ShareTypes.WECHAT_FRIENDS)}
              data-sa-desc="分享入口弹窗"
              data-sa-describe={this.$tracking.serialize({
                active_name: '弹窗展示-微信',
              })}
            >
              <img src={wechatFriendsImage} alt="" />
              <span>微信好友</span>
            </button>
            <button
              type="button"
              class={styles.button}
              onClick={() => this.onClickItem(ShareTypes.WECHAT_ZONE)}
              data-sa-desc="分享入口弹窗"
              data-sa-describe={this.$tracking.serialize({
                active_name: '弹窗展示-朋友圈',
              })}
            >
              <img src={wechatZoneImage} alt="" />
              <span>朋友圈</span>
            </button>
            <button
              type="button"
              class={styles.button}
              onClick={this.onCopyLink}
              data-sa-desc="分享入口弹窗"
              data-sa-describe={this.$tracking.serialize({
                active_name: '弹窗展示-复制链接',
              })}
            >
              <img src={linkImage} alt="" />
              <span>复制链接</span>
            </button>
          </div>
        </Popup>
      );
    },
  }).$mount();

  document.body.appendChild(instance.$el);

  return instance;
}

export const useSharer = () => {
  const instance = getCurrentInstance();

  if (!instance) {
    throw new Error(
      '[share]: Missing current instance. useSharer() must be called inside <script setup> or setup().',
    );
  }

  return instance.proxy.$root.$sharer;
};

export default {
  install(Vue) {
    Vue.prototype.$sharer = createInstance(Vue);
  },
} as PluginObject<never>;
